"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { GUIDE_TOPICS, GUIDE_TOPIC_ORDER, type GuideTopic } from "@/lib/guideTopics";
import { track } from "@/lib/analytics";

/** Serializable card data — guides are read from the filesystem server-side. */
export interface GuideCardData {
  slug: string;
  title: string;
  intro: string;
  topic: GuideTopic;
}

function haystack(g: GuideCardData): string {
  return `${g.title} ${g.intro} ${GUIDE_TOPICS[g.topic].label}`.toLowerCase();
}
function matches(hay: string, tokens: string[]): boolean {
  return tokens.every((tok) => hay.includes(tok));
}

export function GuidesExplorer({ guides }: { guides: GuideCardData[] }) {
  const [active, setActive] = useState<GuideTopic | "all">("all");
  const [query, setQuery] = useState("");

  // Allow deep links like /guides/?topic=rams or /guides/?q=coshh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topic = params.get("topic") as GuideTopic | null;
    if (topic && GUIDE_TOPIC_ORDER.includes(topic)) setActive(topic);
    const q = params.get("q") || params.get("search") || "";
    if (q.trim()) setQuery(q.trim());
  }, []);

  const q = query.trim().toLowerCase();
  const tokens = q ? q.split(/\s+/) : [];
  const searching = tokens.length > 0;

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: guides.length };
    for (const t of GUIDE_TOPIC_ORDER) c[t] = guides.filter((g) => g.topic === t).length;
    return c;
  }, [guides]);

  // When searching, match the whole set (findability beats topic scoping). With no
  // query, the topic pill scopes the list; "all" shows the grouped, topic-led view.
  const filtered = useMemo(
    () =>
      guides.filter((g) => {
        const queryOk = !searching || matches(haystack(g), tokens);
        const topicOk = searching || active === "all" || g.topic === active;
        return queryOk && topicOk;
      }),
    [guides, active, searching, q],
  );
  const resultCount = filtered.length;
  const grouped = !searching && active === "all";

  // Debounced search-intent capture — what people look for and what returns nothing.
  const lastTracked = useRef("");
  useEffect(() => {
    if (!searching || q.length < 2) return;
    const id = setTimeout(() => {
      if (lastTracked.current === q) return;
      lastTracked.current = q;
      track("guide_search", { query: q, results: resultCount });
      if (resultCount === 0) track("guide_search_no_results", { query: q });
    }, 650);
    return () => clearTimeout(id);
  }, [q, searching, resultCount]);

  const groups = grouped
    ? GUIDE_TOPIC_ORDER.map((t) => ({ topic: t, guides: guides.filter((g) => g.topic === t) })).filter((g) => g.guides.length > 0)
    : [];

  function onCardClick(e: React.MouseEvent) {
    const a = (e.target as HTMLElement).closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    track("guide_card_clicked", { query: q, href, topic: active });
  }

  return (
    <div className="bg-surface">
      {/* Filter bar */}
      <div className="border-b border-line bg-surface">
        <div className="mx-auto max-w-7xl px-5 py-6">
          <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides — e.g. RAMS, method statement, COSHH, CDM, permit"
              aria-label="Search guides"
              className="w-full rounded-xl border border-line bg-white px-4 py-3 text-base text-ink focus:border-emerald focus:outline-none"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pill label="All guides" count={counts.all} on={active === "all" && !searching} onClick={() => { setQuery(""); setActive("all"); }} />
            {GUIDE_TOPIC_ORDER.map((t) => (
              <Pill
                key={t}
                label={GUIDE_TOPICS[t].label}
                count={counts[t]}
                on={!searching && active === t}
                onClick={() => {
                  setQuery("");
                  setActive(t);
                  track("guide_filter_changed", { topic: t });
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Grouped, topic-led view (default) */}
      {grouped &&
        groups.map((group, i) => (
          <section key={group.topic} className={i % 2 === 0 ? "bg-mist" : "bg-surface"}>
            <div className="mx-auto max-w-7xl px-5 py-12">
              <h2 className="text-2xl md:text-3xl">{GUIDE_TOPICS[group.topic].label}</h2>
              <p className="mt-2 max-w-2xl text-muted">{GUIDE_TOPICS[group.topic].blurb}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onClickCapture={onCardClick}>
                {group.guides.map((g) => <GuideCard key={g.slug} g={g} />)}
              </div>
            </div>
          </section>
        ))}

      {/* Flat, filtered/searched view */}
      {!grouped && (
        <section className="bg-mist">
          <div className="mx-auto max-w-7xl px-5 py-12">
            <p className="text-sm text-body">
              {resultCount} {resultCount === 1 ? "guide" : "guides"}
              {searching ? <> for &ldquo;{query.trim()}&rdquo;</> : <> · {GUIDE_TOPICS[active as GuideTopic].label}</>}
            </p>
            {resultCount === 0 ? (
              <div className="mt-6 rounded-xl border border-line bg-white p-6 shadow-soft">
                <p className="font-display text-lg font-bold text-ink">No guides match &ldquo;{query.trim()}&rdquo; yet.</p>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-body">
                  Try a broader term, or go straight to the{" "}
                  <Link href="/templates/" className="font-semibold text-emerald-deep underline">template library</Link> or the{" "}
                  <Link href="/builder/" className="font-semibold text-emerald-deep underline">RAMS builder</Link>.
                </p>
              </div>
            ) : (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onClickCapture={onCardClick}>
                {filtered.map((g) => <GuideCard key={g.slug} g={g} />)}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function GuideCard({ g }: { g: GuideCardData }) {
  return (
    <Link href={`/guides/${g.slug}/`} className="card lift shadow-soft p-6">
      <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">{GUIDE_TOPICS[g.topic].label}</div>
      <div className="mt-1 font-display text-xl font-semibold text-ink">{g.title}</div>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted">{g.intro}</p>
      <div className="mt-4 text-sm font-semibold text-emerald-deep">Read the guide →</div>
    </Link>
  );
}

function Pill({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
        on ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-line-2 hover:text-ink"
      }`}
    >
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}
